<template>
  <div class="blog-seo-rank">
    <!-- هدر و فیلترها -->
    <CCard class="mb-3">
      <CCardBody>
        <CRow>
          <CCol md="4">
            <CInput
                label="جستجو"
                placeholder="عنوان یا کلمه کلیدی را وارد کنید..."
                v-model="searchQuery"
                @input="filterPosts"
            >
              <template #prepend>
                <CIcon name="cil-magnifying-glass" />
              </template>
            </CInput>
          </CCol>
          <CCol md="3">
            <CSelect
                label="مرتب‌سازی بر اساس"
                :options="sortOptions"
                v-model="sortBy"
                @change="sortPosts"
            />
          </CCol>
          <CCol md="3">
            <CSelect
                label="فیلتر وضعیت"
                :options="filterOptions"
                v-model="filterStatus"
                @change="filterPosts"
            />
          </CCol>
          <CCol md="2" class="d-flex align-items-end">
            <CButton color="primary" block @click="refreshData">
              <CIcon name="cil-reload" />
              بروزرسانی
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- آمار کلی -->
    <CRow class="mb-3">
      <CCol md="3">
        <CCard class="text-center bg-light">
          <CCardBody>
            <div class="stat-icon text-primary">
              <CIcon name="cil-document" size="xl" />
            </div>
            <div class="stat-value">{{ filteredPosts.length }}</div>
            <div class="stat-label">کل مقالات</div>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol md="3">
        <CCard class="text-center bg-light">
          <CCardBody>
            <div class="stat-icon text-success">
              <CIcon name="cil-star" size="xl" />
            </div>
            <div class="stat-value">{{ excellentCount }}</div>
            <div class="stat-label">عالی (۸۰%+)</div>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol md="3">
        <CCard class="text-center bg-light">
          <CCardBody>
            <div class="stat-icon text-warning">
              <CIcon name="cil-check" size="xl" />
            </div>
            <div class="stat-value">{{ goodCount }}</div>
            <div class="stat-label">خوب (۶۰-۸۰%)</div>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol md="3">
        <CCard class="text-center bg-light">
          <CCardBody>
            <div class="stat-icon text-danger">
              <CIcon name="cil-warning" size="xl" />
            </div>
            <div class="stat-value">{{ needsImprovementCount }}</div>
            <div class="stat-label">نیاز به بهبود (&lt;۶۰%)</div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- کارت‌های مقالات -->
    <div v-if="filteredPosts.length === 0" class="text-center py-5">
      <CIcon name="cil-info" size="xl" />
      <p class="mt-2">هیچ مقاله‌ای با این شرایط یافت نشد</p>
    </div>

    <div v-else class="blog-cards">
      <transition-group name="card-list" tag="div">
        <CCard
            v-for="(item, index) in filteredPosts"
            :key="item.id || index"
            class="blog-card mb-3"
            :class="getCardClass(item.seoScore)"
        >
          <CCardBody>
            <CRow>
              <!-- ستون اصلی اطلاعات -->
              <CCol md="7">
                <div class="d-flex align-items-start">
                  <!-- رتبه -->
                  <div class="rank-badge mr-3">
                    <span class="rank-number">{{ index + 1 }}</span>
                  </div>

                  <!-- اطلاعات اصلی -->
                  <div class="flex-grow-1">
                    <h5 class="blog-title">
                      {{ item.title }}
                      <CBadge
                          :color="getScoreColor(item.seoScore)"
                          class="ml-2"
                          size="lg"
                      >
                        {{ item.seoScore }}%
                      </CBadge>
                    </h5>

                    <div class="blog-meta">
                      <span class="meta-item">
                        <CIcon name="cil-tag" class="meta-icon" />
                        <span class="keyword">{{ item.keyword }}</span>
                      </span>
                      <span class="meta-item">
                        <CIcon name="cil-calendar" class="meta-icon" />
                        {{ item.created_at || 'تاریخ نامشخص' }}
                      </span>
                      <span class="meta-item" v-if="item.category">
                        <CIcon name="cil-folder" class="meta-icon" />
                        {{ item.category }}
                      </span>
                    </div>

                    <!-- متریک‌های اصلی -->
                    <CRow class="mt-2">
                      <CCol md="4">
                        <div class="metric-item">
                          <span class="metric-label">کلمات</span>
                          <span :class="getWordCountClass(item.wordCount)">{{ item.wordCount }}</span>
                        </div>
                      </CCol>
                      <CCol md="4">
                        <div class="metric-item">
                          <span class="metric-label">خوانایی</span>
                          <span :class="getScoreClass(item.readabilityScore)">{{ item.readabilityScore }}%</span>
                        </div>
                      </CCol>
                      <CCol md="4">
                        <div class="metric-item">
                          <span class="metric-label">سئو تکنیکال</span>
                          <span :class="getScoreClass(item.technicalSEO)">{{ item.technicalSEO }}%</span>
                        </div>
                      </CCol>
                    </CRow>
                  </div>
                </div>
              </CCol>

              <!-- ستون متریک‌های دقیق -->
              <CCol md="3">
                <div class="metrics-grid">
                  <div class="metric-cell">
                    <span class="metric-icon">
                      <CIcon name="cil-image" />
                    </span>
                    <span :class="getImageCountClass(item.imageCount)">{{ item.imageCount }}</span>
                    <span class="metric-label-small">تصویر</span>
                  </div>
                  <div class="metric-cell">
                    <span class="metric-icon">
                      <CIcon name="cil-link" />
                    </span>
                    <span :class="getLinkCountClass(item.linkCount)">{{ item.linkCount }}</span>
                    <span class="metric-label-small">لینک</span>
                  </div>
                  <div class="metric-cell">
                    <span class="metric-icon">
                      <CIcon name="cil-arrow-circle-right" />
                    </span>
                    <span :class="getInternalLinkClass(item.internalLinks)">{{ item.internalLinks || 0 }}</span>
                    <span class="metric-label-small">داخلی</span>
                  </div>
                  <div class="metric-cell">
                    <span class="metric-icon">
                      <CIcon name="cil-chart" />
                    </span>
                    <span :class="getDensityClass(item.keywordDensity)">{{ item.keywordDensity }}%</span>
                    <span class="metric-label-small">چگالی</span>
                  </div>
                </div>
              </CCol>

              <!-- ستون عملیات -->
              <CCol md="2" class="d-flex flex-column justify-content-between align-items-end">
                <div class="btn-group-vertical w-100">
                  <CButton
                      color="warning"
                      variant="outline"
                      size="sm"
                      class="mb-1"
                      @click="show_info_seo_modal(item)"
                  >
                    <CIcon name="cil-info" /> جزئیات
                  </CButton>
                  <CButton
                      color="success"
                      variant="outline"
                      size="sm"
                      class="mb-1"
                      @click="show_blog_post(item)"
                  >
                    <CIcon name="cil-eye" /> مشاهده
                  </CButton>
                  <CButton
                      color="primary"
                      variant="outline"
                      size="sm"
                      @click="edit_blog_post(item)"
                  >
                    <CIcon name="cil-pencil" /> ویرایش
                  </CButton>
                </div>

                <!-- وضعیت H1 و ترتیب -->
                <div class="status-badges mt-2">
                  <CBadge
                      :color="item.h1Count === 1 ? 'success' : 'danger'"
                      size="sm"
                      class="ml-1"
                  >
                    H1 {{ item.h1Count }}
                  </CBadge>
                  <CBadge
                      :color="item.hierarchy ? 'success' : 'danger'"
                      size="sm"
                  >
                    {{ item.hierarchy ? '✅ ترتیب' : '❌ ترتیب' }}
                  </CBadge>
                </div>
              </CCol>
            </CRow>

            <!-- نوار پیشرفت کلی -->
            <div class="seo-progress mt-2">
              <div class="d-flex justify-content-between">
                <small class="text-muted">امتیاز کلی سئو</small>
                <small :class="getScoreClass(item.seoScore)">{{ item.seoScore }}%</small>
              </div>
              <CProgress
                  :value="item.seoScore"
                  :max="100"
                  :color="getScoreColor(item.seoScore)"
                  height="8px"
              />
            </div>
          </CCardBody>
        </CCard>
      </transition-group>
    </div>

    <!-- مودال جزئیات سئو -->
    <CModal :show.sync="seo_state_modal" title="📊 اطلاعات جامع سئو" size="xl">
      <div v-if="result_seo" class="seo-details">
        <!-- رتبه‌ها -->
        <CRow class="mb-4">
          <CCol md="3" v-for="(score, key) in result_seo.scores" :key="key">
            <CCard class="text-center">
              <CCardBody>
                <h6>{{ getScoreLabel(key) }}</h6>
                <div class="score-circle" :class="getScoreClass(score)">
                  {{ score }}%
                </div>
                <CProgress :value="score" :max="100" :color="getScoreColor(score)" height="8px" />
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <!-- جزئیات کامل -->
        <CRow>
          <CCol md="6">
            <CCard>
              <CCardHeader>📋 اطلاعات کلی</CCardHeader>
              <CCardBody>
                <table class="table table-sm">
                  <tr v-for="(value, key) in result_seo.stats" :key="key">
                    <td><strong>{{ getStatLabel(key) }}</strong></td>
                    <td>{{ value }}</td>
                  </tr>
                </table>
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md="6">
            <CCard>
              <CCardHeader>📐 ساختار محتوا</CCardHeader>
              <CCardBody>
                <table class="table table-sm">
                  <tr>
                    <td><strong>H1</strong></td>
                    <td :class="result_seo.stats.h1Count === 1 ? 'text-success' : 'text-danger'">
                      {{ result_seo.stats.h1Count }}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>H2</strong></td>
                    <td>{{ result_seo.stats.h2Count }}</td>
                  </tr>
                  <tr>
                    <td><strong>H3</strong></td>
                    <td>{{ result_seo.stats.h3Count || 0 }}</td>
                  </tr>
                  <tr>
                    <td><strong>ترتیب هدینگ</strong></td>
                    <td>
                      <CBadge :color="result_seo.stats.hierarchy ? 'success' : 'danger'">
                        {{ result_seo.stats.hierarchy ? '✅ صحیح' : '❌ خطا' }}
                      </CBadge>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>متا توضیحات</strong></td>
                    <td>
                      <CBadge :color="result_seo.details.hasMetaDescription ? 'success' : 'danger'">
                        {{ result_seo.details.hasMetaDescription ? '✅ دارد' : '❌ ندارد' }}
                      </CBadge>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>طول متا</strong></td>
                    <td>{{ result_seo.details.metaDescriptionLength || 0 }} کاراکتر</td>
                  </tr>
                  <tr>
                    <td><strong>کلمه کلیدی در H1</strong></td>
                    <td>
                      <CBadge :color="result_seo.details.keywordInH1 ? 'success' : 'warning'">
                        {{ result_seo.details.keywordInH1 ? '✅ دارد' : '⚠️ ندارد' }}
                      </CBadge>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>کلمه کلیدی در URL</strong></td>
                    <td>
                      <CBadge :color="result_seo.details.keywordInUrl ? 'success' : 'warning'">
                        {{ result_seo.details.keywordInUrl ? '✅ دارد' : '⚠️ ندارد' }}
                      </CBadge>
                    </td>
                  </tr>
                </table>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <!-- پیام‌ها -->
        <CRow class="mt-3">
          <CCol md="12">
            <CCard>
              <CCardHeader>📝 گزارش تحلیل</CCardHeader>
              <CCardBody>
                <div v-if="result_seo.errors && result_seo.errors.length" class="mb-3">
                  <h6 class="text-danger">❌ خطاها ({{ result_seo.errors.length }})</h6>
                  <ul>
                    <li v-for="error in result_seo.errors" :key="error" class="text-danger">{{ error }}</li>
                  </ul>
                </div>

                <div v-if="result_seo.warnings && result_seo.warnings.length" class="mb-3">
                  <h6 class="text-warning">⚠️ هشدارها ({{ result_seo.warnings.length }})</h6>
                  <ul>
                    <li v-for="warning in result_seo.warnings" :key="warning" class="text-warning">{{ warning }}</li>
                  </ul>
                </div>

                <div v-if="result_seo.successes && result_seo.successes.length" class="mb-3">
                  <h6 class="text-success">✅ موفقیت‌ها ({{ result_seo.successes.length }})</h6>
                  <ul>
                    <li v-for="success in result_seo.successes" :key="success" class="text-success">{{ success }}</li>
                  </ul>
                </div>

                <div v-if="result_seo.suggestions && result_seo.suggestions.length" class="mb-3">
                  <h6 class="text-primary">💡 پیشنهادات بهبود ({{ result_seo.suggestions.length }})</h6>
                  <ul>
                    <li v-for="suggestion in result_seo.suggestions" :key="suggestion" class="text-primary">{{ suggestion }}</li>
                  </ul>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </div>
      <template #footer>
        <CButton color="secondary" @click="seo_state_modal = false">بستن</CButton>
      </template>
    </CModal>
  </div>
</template>

<script >
import { parser_config_in_site, custom_parser_config_in_site } from "@/plugins/editorjs_config";

export default {
  name: 'BlogSeoRank',
  props: {
    blog_posts: Array
  },
  watch: {
    'blog_posts': {
      handler() {
        this.get_score();
      },
      immediate: true,
      deep: true
    }
  },
  data() {
    return {
      seo_state_modal: false,
      result_seo: null,
      parser: null,
      searchQuery: '',
      sortBy: 'seoScore',
      filterStatus: 'all',
      filteredPosts: [],
      sortOptions: [
        { value: 'seoScore', label: 'امتیاز سئو (بیشترین)' },
        { value: 'seoScoreAsc', label: 'امتیاز سئو (کمترین)' },
        { value: 'wordCount', label: 'تعداد کلمات (بیشترین)' },
        { value: 'wordCountAsc', label: 'تعداد کلمات (کمترین)' },
        { value: 'title', label: 'عنوان (الفبا)' },
        { value: 'readabilityScore', label: 'خوانایی (بیشترین)' }
      ],
      filterOptions: [
        { value: 'all', label: 'همه مقالات' },
        { value: 'excellent', label: '🌟 عالی (۸۰%+)' },
        { value: 'good', label: '👍 خوب (۶۰-۸۰%)' },
        { value: 'needsImprovement', label: '⚠️ نیاز به بهبود (&lt;۶۰%)' }
      ]
    };
  },
  computed: {
    excellentCount() {
      return this.filteredPosts.filter(p => p.seoScore >= 80).length;
    },
    goodCount() {
      return this.filteredPosts.filter(p => p.seoScore >= 60 && p.seoScore < 80).length;
    },
    needsImprovementCount() {
      return this.filteredPosts.filter(p => p.seoScore < 60).length;
    }
  },
  mounted() {
    this.parser = new edjsParser(parser_config_in_site, custom_parser_config_in_site, {});
    this.filteredPosts = [...this.blog_posts];
  },
  methods: {
    show_blog_post(item) {
      window.open(localStorage.getItem("web_address") + "blog/" + item.slug, "_blank");

    },
    edit_blog_post(item) {
      this.$router.push({path: '/dashboard/news/edit-news/' + item.source})
    },
    get_score() {
      var self = this;

      this.blog_posts.forEach(function (val) {
        try {
          const parsedContent = val.description ? JSON.parse(val.description) : null;
          const htmlContent = parsedContent ? self.parser.parse(parsedContent) : '';

          val.seo_state = self.analyzeSEO(
              htmlContent,
              val.keyword || '',
              val.short_description || '',
              val.title || '',
              val.slug || ''
          );

          val.seoScore = val.seo_state.seoScore;
          val.technicalSEO = val.seo_state.technicalSEO;
          val.contentQuality = val.seo_state.contentQuality;
          val.readabilityScore = val.seo_state.readabilityScore;
          val.wordCount = val.seo_state.stats.wordCount;
          val.imageCount = val.seo_state.stats.imageCount;
          val.linkCount = val.seo_state.stats.linkCount;
          val.internalLinks = val.seo_state.stats.internalLinks;
          val.externalLinks = val.seo_state.stats.externalLinks;
          val.keywordDensity = val.seo_state.stats.keywordDensity;
          val.h1Count = val.seo_state.stats.h1Count;
          val.h2Count = val.seo_state.stats.h2Count;
          val.hierarchy = val.seo_state.stats.hierarchy;
        } catch (e) {
          console.error('Error analyzing SEO for:', val.title, e);
        }
      });

      this.filteredPosts = [...this.blog_posts];
      this.sortPosts();
    },

    // ==========================================
    // تابع حذف کلمات ربط
    // ==========================================
    removeStopWords(text) {
      if (!text) return [];

      const stopWords = [
        'از', 'با', 'برای', 'در', 'به', 'بر', 'روی', 'زیر', 'کنار',
        'جلوی', 'پشت', 'داخل', 'خارج', 'بالای', 'پایین', 'نزدیک',
        'دور', 'بین', 'میان', 'طریق', 'وسیلۀ', 'توسط', 'بجز', 'غیر',
        'مثل', 'مانند', 'همانند', 'چون', 'چرا', 'زیرا', 'چراکه',
        'که', 'تا', 'مگر', 'آیا', 'چه', 'چگونه', 'چطور', 'کجا',
        'کی', 'چند', 'چقدر', 'کدام', 'کدامین', 'این', 'آن', 'اینها',
        'آنها', 'ما', 'شما', 'او', 'ایشان', 'خود', 'خودی', 'یک', 'دو',
        'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه', 'ده', 'صد', 'هزار',
        'و', 'یا', 'اما', 'پس', 'پس‌از', 'پیش', 'پیش‌از', 'بعد', 'بعداز'
      ];

      const words = text.split(/\s+/).filter(w => w.length > 0);

      return words.filter(word => {
        const cleanWord = word.replace(/[^آ-یa-zA-Z]/g, '').toLowerCase();
        return !stopWords.includes(cleanWord) && cleanWord.length > 0;
      });
    },

    // ==========================================
    // تحلیل سئو (کامل)
    // ==========================================
    analyzeSEO(htmlSample, focusKeyword = "", meta_description, title, slug) {
      const htmlString = `
<html>
<head>
    <meta name="description" content="${meta_description || ''}">
    <title>${title || ''}</title>
</head>
<body>
    <h1>${title || ''}</h1>
    ${htmlSample || ''}
</body>
</html>
`;
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, "text/html");

      const result = {
        seoScore: 0,
        technicalSEO: 0,
        contentQuality: 0,
        readabilityScore: 0,
        scores: {},
        stats: {
          wordCount: 0,
          imageCount: 0,
          linkCount: 0,
          internalLinks: 0,
          externalLinks: 0,
          h1Count: 0,
          h2Count: 0,
          h3Count: 0,
          hierarchy: false,
          keywordDensity: 0,
          keywordCount: 0,
          avgSentenceLength: 0,
          paragraphCount: 0,
          avgParagraphLength: 0
        },
        details: {
          hasMetaDescription: false,
          metaDescriptionLength: 0,
          keywordInH1: false,
          keywordInMeta: false,
          keywordInTitle: false,
          keywordInUrl: false
        },
        errors: [],
        warnings: [],
        successes: [],
        suggestions: []
      };

      // ==========================================
      // 1. محاسبه آمار پایه
      // ==========================================
      const bodyText = doc.body.innerText || "";
      const words = bodyText.split(/\s+/).filter(word => word.length > 0);
      result.stats.wordCount = words.length;

      const paragraphs = bodyText.split(/\n+/).filter(p => p.trim().length > 0);
      result.stats.paragraphCount = paragraphs.length;
      result.stats.avgParagraphLength = result.stats.paragraphCount > 0 ?
          Math.round(result.stats.wordCount / result.stats.paragraphCount) : 0;

      const sentences = bodyText.split(/[.!?]+/).filter(s => s.trim().length > 0);
      const sentenceCount = sentences.length;
      result.stats.avgSentenceLength = sentenceCount > 0 ?
          Math.round(result.stats.wordCount / sentenceCount) : 0;

      // ==========================================
      // 2. خوانایی (Readability)
      // ==========================================
      let readabilityScore = 0;

      if (result.stats.wordCount >= 1500) {
        readabilityScore += 30;
        result.successes.push(`✅ محتوای طولانی و کامل (${result.stats.wordCount} کلمه)`);
      } else if (result.stats.wordCount >= 700) {
        readabilityScore += 20;
        result.successes.push(`✅ محتوای با طول مناسب (${result.stats.wordCount} کلمه)`);
      } else if (result.stats.wordCount >= 300) {
        readabilityScore += 10;
        result.warnings.push(`⚠️ محتوا می‌تواند طولانی‌تر باشد (${result.stats.wordCount} کلمه)`);
      } else {
        result.errors.push(`❌ محتوای خیلی کوتاه (${result.stats.wordCount} کلمه)`);
      }

      if (result.stats.avgSentenceLength > 0 && result.stats.avgSentenceLength <= 20) {
        readabilityScore += 20;
        result.successes.push(`✅ جملات با طول مناسب (میانگین ${result.stats.avgSentenceLength} کلمه)`);
      } else if (result.stats.avgSentenceLength > 20 && result.stats.avgSentenceLength <= 30) {
        readabilityScore += 10;
        result.warnings.push(`⚠️ جملات کمی طولانی هستند (میانگین ${result.stats.avgSentenceLength} کلمه)`);
      } else if (result.stats.avgSentenceLength > 30) {
        result.warnings.push(`⚠️ جملات خیلی طولانی هستند (میانگین ${result.stats.avgSentenceLength} کلمه)`);
      }

      if (result.stats.avgParagraphLength > 0 && result.stats.avgParagraphLength <= 100) {
        readabilityScore += 15;
        result.successes.push(`✅ پاراگراف‌های با طول مناسب (میانگین ${result.stats.avgParagraphLength} کلمه)`);
      } else if (result.stats.avgParagraphLength > 100) {
        result.warnings.push(`⚠️ پاراگراف‌ها خیلی طولانی هستند (میانگین ${result.stats.avgParagraphLength} کلمه)`);
      }

      result.readabilityScore = Math.min(readabilityScore, 100);

      // ==========================================
      // 3. کیفیت محتوا
      // ==========================================
      let contentQuality = 0;

      result.stats.imageCount = doc.getElementsByTagName("img").length;
      if (result.stats.imageCount >= 4) {
        contentQuality += 25;
        result.successes.push(`✅ ${result.stats.imageCount} تصویر با کیفیت مناسب`);
      } else if (result.stats.imageCount >= 1) {
        contentQuality += 15;
        result.warnings.push(`⚠️ تعداد تصاویر کم است (${result.stats.imageCount} تصویر)`);
      } else {
        result.warnings.push(`⚠️ هیچ تصویری در محتوا وجود ندارد`);
      }

      const images = doc.getElementsByTagName("img");
      let imagesWithAlt = 0;
      Array.from(images).forEach(img => {
        if (img.getAttribute("alt")) imagesWithAlt++;
      });

      if (images.length > 0 && imagesWithAlt === images.length) {
        contentQuality += 15;
        result.successes.push(`✅ تمام تصاویر دارای تگ alt هستند`);
      } else if (images.length > 0) {
        result.warnings.push(`⚠️ ${images.length - imagesWithAlt} تصویر بدون تگ alt`);
      }

      result.stats.h1Count = doc.getElementsByTagName("h1").length;
      if (result.stats.h1Count === 1) {
        contentQuality += 20;
        result.successes.push(`✅ یک تگ H1 مناسب وجود دارد`);
      } else if (result.stats.h1Count > 1) {
        result.errors.push(`❌ بیش از یک تگ H1 وجود دارد (${result.stats.h1Count} عدد)`);
      } else {
        result.errors.push(`❌ هیچ تگ H1 وجود ندارد`);
      }

      result.stats.h2Count = doc.getElementsByTagName("h2").length;
      if (result.stats.h2Count >= 4) {
        contentQuality += 20;
        result.successes.push(`✅ ${result.stats.h2Count} تگ H2 برای ساختاردهی مناسب`);
      } else if (result.stats.h2Count >= 1) {
        contentQuality += 10;
        result.warnings.push(`⚠️ تعداد H2 کم است (${result.stats.h2Count} عدد)`);
      } else {
        result.warnings.push(`⚠️ هیچ تگ H2 برای ساختاردهی وجود ندارد`);
      }

      result.stats.h3Count = doc.getElementsByTagName("h3").length;
      result.stats.hierarchy = this.validateHeadingHierarchy(htmlString);
      if (result.stats.hierarchy) {
        contentQuality += 10;
        result.successes.push(`✅ ترتیب هدینگ‌ها منطقی و صحیح است`);
      } else {
        result.errors.push(`❌ ترتیب هدینگ‌ها نامنظم است`);
      }

      result.contentQuality = Math.min(contentQuality, 100);

      // ==========================================
      // 4. سئو تکنیکال
      // ==========================================
      let technicalSEO = 0;

      const metaDesc = doc.querySelector('meta[name="description"]');
      if (metaDesc) {
        result.details.hasMetaDescription = true;
        result.details.metaDescriptionLength = metaDesc.getAttribute("content")?.length || 0;

        if (result.details.metaDescriptionLength >= 120 && result.details.metaDescriptionLength <= 160) {
          technicalSEO += 20;
          result.successes.push(`✅ متا توضیحات با طول مناسب (${result.details.metaDescriptionLength} کاراکتر)`);
        } else if (result.details.metaDescriptionLength > 0) {
          technicalSEO += 10;
          result.warnings.push(`⚠️ طول متا توضیحات (${result.details.metaDescriptionLength} کاراکتر) خارج از محدوده ایده‌آل (120-160)`);
        }
      } else {
        result.errors.push(`❌ متا توضیحات وجود ندارد`);
      }

      if (focusKeyword && metaDesc) {
        const metaContent = metaDesc.getAttribute("content") || "";
        result.details.keywordInMeta = metaContent.toLowerCase().includes(focusKeyword.toLowerCase());
        if (result.details.keywordInMeta) {
          technicalSEO += 10;
          result.successes.push(`✅ کلمه کلیدی "${focusKeyword}" در متا توضیحات وجود دارد`);
        } else {
          result.warnings.push(`⚠️ کلمه کلیدی "${focusKeyword}" در متا توضیحات وجود ندارد`);
        }
      }

      if (focusKeyword && result.stats.h1Count > 0) {
        const h1Text = doc.getElementsByTagName("h1")[0]?.innerText || "";
        result.details.keywordInH1 = h1Text.toLowerCase().includes(focusKeyword.toLowerCase());
        if (result.details.keywordInH1) {
          technicalSEO += 15;
          result.successes.push(`✅ کلمه کلیدی "${focusKeyword}" در H1 وجود دارد`);
        } else {
          result.warnings.push(`⚠️ کلمه کلیدی "${focusKeyword}" در H1 وجود ندارد`);
        }
      }

      const titleTag = doc.querySelector("title");
      if (titleTag && focusKeyword) {
        result.details.keywordInTitle = titleTag.innerText.toLowerCase().includes(focusKeyword.toLowerCase());
        if (result.details.keywordInTitle) {
          technicalSEO += 10;
          result.successes.push(`✅ کلمه کلیدی "${focusKeyword}" در title وجود دارد`);
        } else {
          result.warnings.push(`⚠️ کلمه کلیدی "${focusKeyword}" در title وجود ندارد`);
        }
      }

      if (slug && focusKeyword) {
        result.details.keywordInUrl = slug.toLowerCase().includes(focusKeyword.toLowerCase().replace(/\s+/g, '-'));
        if (result.details.keywordInUrl) {
          technicalSEO += 10;
          result.successes.push(`✅ کلمه کلیدی "${focusKeyword}" در URL وجود دارد`);
        } else {
          result.warnings.push(`⚠️ کلمه کلیدی "${focusKeyword}" در URL وجود ندارد`);
        }
      }

      // ==========================================
      // محاسبه چگالی کلمه کلیدی (اصلاح شده)
      // ==========================================
      if (focusKeyword && result.stats.wordCount > 0) {
        const normalizedText = bodyText.toLowerCase();

        // حذف کلمات ربط از کلمه کلیدی
        const cleanKeyword = this.removeStopWords(focusKeyword).join(' ');
        const normalizedKeyword = cleanKeyword.toLowerCase();

        let keywordCount = 0;

        // روش 1: جستجوی عبارت کامل با RegExp
        try {
          const regex = new RegExp(normalizedKeyword.replace(/\s+/g, '\\s+'), 'gi');
          const matches = normalizedText.match(regex);
          keywordCount = matches ? matches.length : 0;
        } catch (e) {
          keywordCount = 0;
        }

        // روش 2: اگر عبارت کامل پیدا نشد، کلمات را جداگانه بررسی کن
        if (keywordCount === 0) {
          const keywordWords = focusKeyword.toLowerCase().split(/\s+/);
          let totalMatches = 0;
          let validWords = 0;

          keywordWords.forEach(kw => {
            const cleanKw = this.removeStopWords(kw).join('');
            if (cleanKw && cleanKw.length > 1) {
              try {
                const wordRegex = new RegExp(cleanKw, 'gi');
                const wordMatches = normalizedText.match(wordRegex);
                if (wordMatches) {
                  totalMatches += wordMatches.length;
                  validWords++;
                }
              } catch (e) {
                // خطا را نادیده بگیر
              }
            }
          });

          if (validWords > 0) {
            keywordCount = Math.round(totalMatches / validWords);
          }
        }

        // روش 3: اگر هیچ کدام کار نکرد، از indexOf استفاده کن
        if (keywordCount === 0) {
          let searchKeyword = focusKeyword.toLowerCase();
          let count = 0;
          let pos = normalizedText.indexOf(searchKeyword);
          while (pos !== -1) {
            count++;
            pos = normalizedText.indexOf(searchKeyword, pos + searchKeyword.length);
          }
          keywordCount = count;
        }

        result.stats.keywordCount = keywordCount;
        result.stats.keywordDensity = result.stats.wordCount > 0
            ? Math.round((keywordCount / result.stats.wordCount) * 100 * 100) / 100
            : 0;

        // اضافه کردن به نتایج
        if (result.stats.keywordDensity >= 1 && result.stats.keywordDensity <= 3) {
          technicalSEO += 15;
          result.successes.push(`✅ چگالی کلمه کلیدی مناسب (${result.stats.keywordDensity}% - ${keywordCount} بار تکرار)`);
        } else if (result.stats.keywordDensity > 0 && result.stats.keywordDensity < 5) {
          technicalSEO += 5;
          result.warnings.push(`⚠️ چگالی کلمه کلیدی (${result.stats.keywordDensity}% - ${keywordCount} بار تکرار) در محدوده قابل قبول اما بهتر است 1-3% باشد`);
        } else if (result.stats.keywordDensity >= 5) {
          result.warnings.push(`⚠️ چگالی کلمه کلیدی زیاد است (${result.stats.keywordDensity}% - ${keywordCount} بار تکرار) - ممکن است به عنوان کیورد استافینگ تلقی شود`);
        } else if (result.stats.keywordDensity === 0 && focusKeyword) {
          result.warnings.push(`⚠️ کلمه کلیدی "${focusKeyword}" در محتوا پیدا نشد!`);
        }
      }

      const links = doc.getElementsByTagName("a");
      result.stats.linkCount = links.length;

      let internalLinks = 0;
      let externalLinks = 0;

      Array.from(links).forEach(link => {
        const href = link.getAttribute("href") || "";
        if (href.startsWith("/") || href.includes(window?.location?.hostname || "")) {
          internalLinks++;
        } else if (href.startsWith("http")) {
          externalLinks++;
        }
      });

      result.stats.internalLinks = internalLinks;
      result.stats.externalLinks = externalLinks;

      if (internalLinks >= 3) {
        technicalSEO += 10;
        result.successes.push(`✅ ${internalLinks} لینک داخلی برای ارتباط موضوعی مناسب`);
      } else if (internalLinks > 0) {
        technicalSEO += 5;
        result.warnings.push(`⚠️ تعداد لینک‌های داخلی کم است (${internalLinks} عدد)`);
      } else {
        result.warnings.push(`⚠️ هیچ لینک داخلی وجود ندارد`);
      }

      result.technicalSEO = Math.min(technicalSEO, 100);

      // ==========================================
      // 5. امتیاز کلی
      // ==========================================
      const seoScore = Math.round(
          (result.technicalSEO * 0.4) +
          (result.contentQuality * 0.35) +
          (result.readabilityScore * 0.25)
      );

      result.seoScore = Math.min(seoScore, 100);
      result.scores = {
        seoScore: result.seoScore,
        technicalSEO: result.technicalSEO,
        contentQuality: result.contentQuality,
        readabilityScore: result.readabilityScore
      };

      // ==========================================
      // 6. پیشنهادات
      // ==========================================
      if (result.seoScore >= 80) {
        result.successes.push(`🎉 رتبه سئوی عالی! محتوا به خوبی بهینه شده است.`);
      } else if (result.seoScore >= 60) {
        result.suggestions.push(`📈 رتبه سئوی خوب. با اعمال پیشنهادات بالا می‌توانید آن را بهبود دهید.`);
      } else if (result.seoScore >= 40) {
        result.suggestions.push(`⚠️ رتبه سئوی متوسط. لطفاً خطاها و هشدارها را بررسی و رفع کنید.`);
      } else {
        result.suggestions.push(`🚨 رتبه سئوی پایین. نیاز به بازنگری اساسی در محتوا و ساختار دارد.`);
      }

      if (result.stats.wordCount < 700) {
        result.suggestions.push(`📝 محتوا را به حداقل 700 کلمه افزایش دهید (فعلاً ${result.stats.wordCount} کلمه)`);
      }

      if (result.stats.imageCount < 3) {
        result.suggestions.push(`🖼️ حداقل 3 تصویر مرتبط با محتوا اضافه کنید (فعلاً ${result.stats.imageCount} تصویر)`);
      }

      if (result.stats.internalLinks < 2) {
        result.suggestions.push(`🔗 حداقل 2 لینک داخلی به صفحات مرتبط اضافه کنید (فعلاً ${result.stats.internalLinks} لینک)`);
      }

      if (!result.details.keywordInH1 && focusKeyword) {
        result.suggestions.push(`📌 کلمه کلیدی "${focusKeyword}" را به H1 اضافه کنید`);
      }

      if (!result.details.keywordInMeta && focusKeyword) {
        result.suggestions.push(`📌 کلمه کلیدی "${focusKeyword}" را به متا توضیحات اضافه کنید`);
      }

      return result;
    },

    validateHeadingHierarchy(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');

      let lastLevel = 0;
      let hasH1 = false;

      for (const heading of headings) {
        const currentLevel = parseInt(heading.tagName.substring(1));

        if (currentLevel === 1) {
          if (hasH1) return false;
          hasH1 = true;
        }

        if (currentLevel > lastLevel + 1) return false;
        lastLevel = currentLevel;
      }

      return hasH1;
    },

    // ==========================================
    // فیلتر و سورت
    // ==========================================
    filterPosts() {
      let filtered = [...this.blog_posts];

      // فیلتر جستجو
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(item =>
            (item.title && item.title.toLowerCase().includes(query)) ||
            (item.keyword && item.keyword.toLowerCase().includes(query))
        );
      }

      // فیلتر وضعیت
      if (this.filterStatus === 'excellent') {
        filtered = filtered.filter(item => item.seoScore >= 80);
      } else if (this.filterStatus === 'good') {
        filtered = filtered.filter(item => item.seoScore >= 60 && item.seoScore < 80);
      } else if (this.filterStatus === 'needsImprovement') {
        filtered = filtered.filter(item => item.seoScore < 60);
      }

      this.filteredPosts = filtered;
      this.sortPosts();
    },

    sortPosts() {
      const sortField = this.sortBy.replace('Asc', '');
      const isAscending = this.sortBy.includes('Asc');

      this.filteredPosts.sort((a, b) => {
        let valA = a[sortField] || 0;
        let valB = b[sortField] || 0;

        if (typeof valA === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }

        if (isAscending) {
          return valA > valB ? 1 : -1;
        } else {
          return valA < valB ? 1 : -1;
        }
      });
    },

    refreshData() {
      this.get_score();
    },

    // ==========================================
    // توابع کمکی
    // ==========================================
    getCardClass(score) {
      if (score >= 80) return 'card-excellent';
      if (score >= 60) return 'card-good';
      return 'card-needs-improvement';
    },

    getScoreColor(score) {
      if (score >= 80) return 'success';
      if (score >= 60) return 'warning';
      return 'danger';
    },

    getScoreClass(score) {
      if (score >= 80) return 'text-success';
      if (score >= 60) return 'text-warning';
      return 'text-danger';
    },

    getScoreLabel(key) {
      const labels = {
        seoScore: 'سئو کلی',
        technicalSEO: 'سئو تکنیکال',
        contentQuality: 'کیفیت محتوا',
        readabilityScore: 'خوانایی'
      };
      return labels[key] || key;
    },

    getStatLabel(key) {
      const labels = {
        wordCount: 'تعداد کلمات',
        imageCount: 'تعداد تصاویر',
        linkCount: 'تعداد لینک‌ها',
        internalLinks: 'لینک‌های داخلی',
        externalLinks: 'لینک‌های خارجی',
        h1Count: 'تعداد H1',
        h2Count: 'تعداد H2',
        h3Count: 'تعداد H3',
        hierarchy: 'ترتیب هدینگ',
        keywordDensity: 'چگالی کلمه کلیدی',
        keywordCount: 'تعداد تکرار کلمه کلیدی',
        avgSentenceLength: 'میانگین طول جملات',
        paragraphCount: 'تعداد پاراگراف‌ها',
        avgParagraphLength: 'میانگین طول پاراگراف'
      };
      return labels[key] || key;
    },

    getWordCountClass(count) {
      if (count >= 1500) return 'text-success font-weight-bold';
      if (count >= 700) return 'text-warning font-weight-bold';
      return 'text-danger font-weight-bold';
    },

    getImageCountClass(count) {
      if (count >= 4) return 'text-success font-weight-bold';
      if (count >= 1) return 'text-warning font-weight-bold';
      return 'text-danger font-weight-bold';
    },

    getLinkCountClass(count) {
      if (count >= 4) return 'text-success font-weight-bold';
      if (count >= 1) return 'text-warning font-weight-bold';
      return 'text-danger font-weight-bold';
    },

    getInternalLinkClass(count) {
      if (count >= 3) return 'text-success font-weight-bold';
      if (count >= 1) return 'text-warning font-weight-bold';
      return 'text-danger font-weight-bold';
    },

    getDensityClass(density) {
      if (density >= 1 && density <= 3) return 'text-success font-weight-bold';
      if (density > 0 && density < 5) return 'text-warning font-weight-bold';
      return 'text-danger font-weight-bold';
    },

    show_info_seo_modal(item) {
      this.result_seo = item.seo_state;
      this.seo_state_modal = true;
    },

  }
}
</script>

<style scoped>
.blog-seo-rank {
  direction: rtl;
  text-align: right;
}

/* ==========================================
   آمار
   ========================================== */
.stat-icon {
  font-size: 2rem;
  margin-bottom: 5px;
}

.stat-value {
  font-size: 2rem;
  font-weight: bold;
}

.stat-label {
  color: #6c757d;
  font-size: 0.9rem;
}

/* ==========================================
   کارت‌ها
   ========================================== */
.blog-card {
  transition: all 0.3s ease;
  border-right: 4px solid #e9ecef;
}

.blog-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.card-excellent {
  border-right-color: #28a745;
  background: linear-gradient(to right, #ffffff, #f0fff4);
}

.card-good {
  border-right-color: #ffc107;
  background: linear-gradient(to right, #ffffff, #fffcf0);
}

.card-needs-improvement {
  border-right-color: #dc3545;
  background: linear-gradient(to right, #ffffff, #fff5f5);
}

/* ==========================================
   رتبه
   ========================================== */
.rank-badge {
  width: 36px;
  height: 36px;
  background: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rank-number {
  font-weight: bold;
  color: #6c757d;
  font-size: 0.9rem;
}

/* ==========================================
   متا اطلاعات
   ========================================== */
.blog-title {
  font-size: 1.1rem;
  margin-bottom: 4px;
}

.blog-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
}

.meta-item {
  font-size: 0.85rem;
  color: #6c757d;
}

.meta-icon {
  margin-left: 4px;
  font-size: 0.8rem;
}

.keyword {
  color: #0d6efd;
  font-weight: 500;
}

/* ==========================================
   متریک‌ها
   ========================================== */
.metric-item {
  display: flex;
  flex-direction: column;
}

.metric-label {
  font-size: 0.7rem;
  color: #6c757d;
  text-transform: uppercase;
}

.metric-item span:last-child {
  font-size: 1.1rem;
  font-weight: 600;
}

.metrics-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.metric-cell {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 8px;
  text-align: center;
}

.metric-cell .metric-icon {
  display: block;
  color: #6c757d;
  font-size: 1rem;
}

.metric-cell span:not(.metric-icon):not(.metric-label-small) {
  font-size: 1.1rem;
  font-weight: 600;
  display: block;
}

.metric-label-small {
  font-size: 0.6rem;
  color: #6c757d;
  display: block;
  margin-top: 2px;
}

/* ==========================================
   وضعیت بدج‌ها
   ========================================== */
.status-badges {
  display: flex;
  gap: 4px;
  justify-content: flex-end;
}

/* ==========================================
   نوار پیشرفت
   ========================================== */
.seo-progress {
  border-top: 1px solid #e9ecef;
  padding-top: 8px;
  margin-top: 8px;
}

/* ==========================================
   انیمیشن کارت‌ها
   ========================================== */
.card-list-move,
.card-list-enter-active,
.card-list-leave-active {
  transition: all 0.5s ease;
}

.card-list-enter-from,
.card-list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.card-list-leave-active {
  position: absolute;
}

/* ==========================================
   مودال
   ========================================== */
.score-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px auto;
  font-size: 24px;
  font-weight: bold;
  border: 4px solid #e9ecef;
}

.score-circle.text-success {
  border-color: #28a745;
  color: #28a745;
}

.score-circle.text-warning {
  border-color: #ffc107;
  color: #ffc107;
}

.score-circle.text-danger {
  border-color: #dc3545;
  color: #dc3545;
}

/* ==========================================
   کلاس‌های کمکی
   ========================================== */
.text-success {
  color: #28a745 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.font-weight-bold {
  font-weight: 700 !important;
}

.ml-2 {
  margin-right: 0.5rem !important;
}

.mr-1 {
  margin-left: 0.25rem !important;
}

.mr-2 {
  margin-left: 0.5rem !important;
}

.mr-3 {
  margin-left: 1rem !important;
}

.mt-2 {
  margin-top: 0.5rem !important;
}

.mb-1 {
  margin-bottom: 0.25rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* ==========================================
   ریسپانسیو
   ========================================== */
@media (max-width: 768px) {
  .metrics-grid {
    grid-template-columns: 1fr 1fr;
  }

  .blog-title {
    font-size: 0.95rem;
  }

  .rank-badge {
    width: 28px;
    height: 28px;
  }

  .rank-number {
    font-size: 0.7rem;
  }
}
</style>